import { createContext, useContext } from 'react';

import type { EditableField } from '@/components/EditFieldModal/_fields/editableFields.types';
import type { Picklists } from '@/core/dcr/picklists';
import type {
	DcrAddressInput,
	DcrLicenseInput,
	DcrParentHcoInput,
	EntityType,
	ParentHco
} from '@/core/graphql/generated/types';

export type InlineEditEntity = {
	vid_V: string | null | undefined;
	id: string | null | undefined;
	countryId: string | null | undefined;
	statusCda_VId: string | null | undefined;
};

export type DraftFieldChange = {
	fieldName: string;
	inputType: 'text' | 'phone' | 'picklist' | 'multiPicklist' | 'boolean';
	value: string | boolean | Array<{ id?: string; name?: string }> | null;
	displayValue?: string | null;
	picklistKey?: string;
	isNewField?: boolean;
};

export type SubmitFieldArgs = {
	change: DraftFieldChange;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitFieldsArgs = {
	changes: DraftFieldChange[];
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitAddressArgs = {
	addressInput: DcrAddressInput;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitLicenseArgs = {
	licenseInput: DcrLicenseInput;
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type SubmitAffiliationArgs = {
	affiliationInputs: DcrParentHcoInput[];
	notes?: string;
	thirdPartyConfirmed: boolean;
	attachmentIds?: string[];
};

export type AddEntityKind = 'address' | 'license' | 'affiliation';

export type AddEntityPrefill = {
	affiliation?: {
		replaceExisting?: boolean;
		replaceAffiliationVid?: string;
	};
};

export type EntityCounts = {
	affiliations: number;
	addresses: number;
	licenses: number;
};

export type InlineEditContextValue = {
	enabled: boolean;
	canRequestChange: boolean;
	entity: InlineEditEntity | null;
	entityType: EntityType;
	picklists: Partial<Picklists>;
	picklistsLoading: boolean;
	unavailableFieldsLoading: boolean;
	unavailableFields: string[];
	unavailableFieldIds: ReadonlySet<string>;
	activeFieldName: string | null;
	activationInitialActive: boolean | null;
	pendingActivation: string | null;
	requestActivate: (
		fieldName: string,
		options?: { initialActive?: boolean }
	) => void;
	requestDeactivate: (fieldName: string) => void;
	requestCloseActive: () => void;
	setActiveDirty: (isDirty: boolean) => void;
	getActiveDirty: () => boolean;
	confirmPendingActivation: () => void;
	cancelPendingActivation: () => void;
	submitField: (
		args: SubmitFieldArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitFields: (
		args: SubmitFieldsArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitAddress: (
		args: SubmitAddressArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitLicense: (
		args: SubmitLicenseArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	submitAffiliation: (
		args: SubmitAffiliationArgs
	) => Promise<{ ok: true } | { ok: false; error: string }>;
	requestAddEntityModal: (
		kind: AddEntityKind,
		prefill?: AddEntityPrefill
	) => void;
	entityModalKind: AddEntityKind | null;
	addEntityPrefill: AddEntityPrefill | null;
	closeAddEntityModal: () => void;
	entityCounts: EntityCounts | null;
	isRequestChangeOpen: boolean;
	openRequestChange: () => void;
	closeRequestChange: () => void;
	editableFields: EditableField[];
	getEditableField: (fieldName: string) => EditableField | undefined;
	knownAffiliations: ParentHco[];
	registerAffiliations: (affiliations: ParentHco[]) => void;
};

export const InlineEditContext = createContext<InlineEditContextValue | null>(
	null
);

export const useInlineEditContext = () => useContext(InlineEditContext);
